import { decode } from 'jsonwebtoken';
import { getUniqueQueryString, safeParseInt } from '../lib/lib';
import { QuestionUniqueNames } from './enums';
import { tenHoursInMilliseconds } from './constants';

export const sessionKeys = {
	refreshToken: 'refreshToken',
	accessToken: 'accessToken',
	clientId: 'clientId',
	clientUniqueId: 'clientUniqueId',
	surveyUniqueId: 'surveyUniqueId',
	dependentQuestionAnswers: 'dependentQuestionAnswers',
	questionId: 'questionId',
	wrongPasswordAttempts: 'wrongPasswordAttempts',
	dismissEnablePushNotificationsModalTime: 'dismissEnablePushNotificationsModalTime',
	redirectUrl: 'redirectUrl',
	publicClientProgress: 'publicProgress',
	publicClientUniqueId: 'publicClientUniqueId',
	registrationData: 'registrationData',
	userOfficeCountry: 'userOfficeCountry',
	userEmail: 'userEmail',
	dashboardActivetTab: 'dashboardActiveTab',
	libraryActiveTab: 'libraryActiveTab',
	podcastActiveTab: 'podcastActiveTab',
	progress12CompletedVideoIndices: 'progress12CompletedVideoIndices',
	bookDetailScrollPosition: 'bookDetailScrollPosition',
	documentVaultPinVerified: 'documentVaultPinVerified',
	wrongDocumentVaultPinAttempts: 'wrongDocumentVaultPinAttempts',
	documentVaultId: 'documentVaultId',
	documentVaultShouldRefresh: 'documentVaultShouldRefresh',
	lastEventProfileModalShown: 'lastProfileModalShown',
	eventPageActiveTab: 'eventPageActiveTab',
	joyrideEventTourSkipTimestamp: 'joyride:eventTourSkipTimestamp',
	joyrideEventTourIntroModalShown: 'joyride:eventTourIntroModalShown',
	communityPageActiveTab: 'communityPageActiveTab',
	lastMonthStepsSynced: 'lastMonthStepsSynced',
	purpleFileVisited: 'purpleFileVisited'
};

export const isLoggedIn = (): boolean => localStorage.getItem(sessionKeys.accessToken) !== null;

export const getRedirectUrl = (): string => localStorage.getItem(sessionKeys.redirectUrl);

export const saveRedirectUrl = (redirectUrl: string): void =>
	localStorage.setItem(sessionKeys.redirectUrl, redirectUrl);

export const getRefreshToken = (): string => localStorage.getItem(sessionKeys.refreshToken);

export const getAccessToken = (): string => localStorage.getItem(sessionKeys.accessToken);

export const saveTokens = (refreshToken: string, accessToken: string): void => {
	localStorage.setItem(sessionKeys.refreshToken, refreshToken);
	localStorage.setItem(sessionKeys.accessToken, accessToken);
};

export const saveAccessToken = (accessToken: string): void =>
	localStorage.setItem(sessionKeys.accessToken, accessToken);

export const getClientId = (): string => localStorage.getItem(sessionKeys.clientId) ?? getClientIdFromAccessToken();

export const setClientIdToLocalStorage = (clientId: string) => localStorage.setItem(sessionKeys.clientId, clientId);

export const getUserEmailFromLocalStorage = (): string => localStorage.getItem(sessionKeys.userEmail);

export const setUserEmailToLocalStorage = (email: string) => localStorage.setItem(sessionKeys.userEmail, email);

export const getPurpleFileVisitedFromLocalStorage = (): boolean =>
	localStorage.getItem(sessionKeys.purpleFileVisited) !== null;

export const setPurpleFileVisitedToLocalStorage = (): void =>
	localStorage.setItem(sessionKeys.purpleFileVisited, 'true');

const getClientIdFromAccessToken = () => {
	const token = getAccessToken();
	if (!token) return null;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const decoded = decode(token) as any;
	if (!decoded) return null; // Handle case where decode returns null

	setClientIdToLocalStorage(decoded?.clientId);
	return decoded?.clientId;
};

export const getClientUniqueId = (): string => localStorage.getItem(sessionKeys.clientUniqueId);

export const setClientUniqueId = (clientUniqueId: string) =>
	localStorage.setItem(sessionKeys.clientUniqueId, clientUniqueId);

export const getSurveyUniqueId = (): string => localStorage.getItem(sessionKeys.surveyUniqueId);

export const setSurveyUniqueId = (surveyUniqueId: string) =>
	localStorage.setItem(sessionKeys.surveyUniqueId, surveyUniqueId);

export const getSurveyUniqueIdFromQuery = (location: Location) => {
	return getUniqueQueryString(location.search, sessionKeys.surveyUniqueId);
};

export const setAgeGroupToLocalStorage = (ageGroup: string) =>
	setQuestionAnswerToLocalStorage(QuestionUniqueNames.AgeGroup, ageGroup);

export const setIsBusinessOwnerToLocalStorage = (isBusinessOwner: string) =>
	setQuestionAnswerToLocalStorage(QuestionUniqueNames.IsBusinessOwner, isBusinessOwner);

export const getQuestionAnswerFromLocalStorage = (questionUniqueName: string): string => {
	const answers: QuestionAnswer[] = JSON.parse(localStorage.getItem(sessionKeys.dependentQuestionAnswers));
	return answers.find((x) => x.questionUniqueName === questionUniqueName)?.answer;
};

export const setQuestionAnswerToLocalStorage = (questionUniqueName: string, answer: string) => {
	const answers: QuestionAnswer[] = JSON.parse(localStorage.getItem(sessionKeys.dependentQuestionAnswers)) ?? [];
	const existingAnswer = answers.find((x) => x.questionUniqueName === questionUniqueName);
	if (existingAnswer) {
		existingAnswer.answer = answer;
	} else {
		answers.push({ questionUniqueName, answer });
	}
	localStorage.setItem(sessionKeys.dependentQuestionAnswers, JSON.stringify(answers));
};

export const getQuestionIdFromLocalStorage = () => safeParseInt(localStorage.getItem(sessionKeys.questionId));

export const setQuestionIdToLocalStorage = (questionId: string) =>
	localStorage.setItem(sessionKeys.questionId, questionId);

export const getWrongPasswordAttemptsFromLocalStorage = (): number =>
	safeParseInt(localStorage.getItem(sessionKeys.wrongPasswordAttempts));

export const setWrongPasswordAttemptsToLocalStorage = (count: number) =>
	localStorage.setItem(sessionKeys.wrongPasswordAttempts, count.toString());

export const getDismissEnablePushNotificationsModalTimeFromLocalStorage = (): number =>
	safeParseInt(localStorage.getItem(sessionKeys.dismissEnablePushNotificationsModalTime));

export const setDismissEnablePushNotificationsModalTimeToLocalStorage = (timeInMilliseconds: number) =>
	localStorage.setItem(sessionKeys.dismissEnablePushNotificationsModalTime, timeInMilliseconds.toString());

export const setPublicClientGuidToLocalStorage = (clientGuid: string) =>
	localStorage.setItem(sessionKeys.publicClientUniqueId, clientGuid);

export const getPublicClientGuidFromLocalStorage = (): string => localStorage.getItem(sessionKeys.publicClientUniqueId);

export const getPublicClientGuidFromQuery = (location: { search: string }) =>
	getUniqueQueryString(location.search, sessionKeys.publicClientUniqueId);

export const setRegistrationDataToLocalStorage = (registerData: string) =>
	localStorage.setItem(sessionKeys.registrationData, registerData);

export const getRegistrationDataFromLocalStorage = (): string => localStorage.getItem(sessionKeys.registrationData);

export const getAppUserOfficeCountryFromLocalStorage = (): string =>
	localStorage.getItem(sessionKeys.userOfficeCountry);

export const setAppUserOfficeCountryToLocalStorage = (country: string) =>
	localStorage.setItem(sessionKeys.userOfficeCountry, country);

export const getDashboardActiveTabFromLocalStorage = (): string =>
	localStorage.getItem(sessionKeys.dashboardActivetTab);

export const setDashboardActiveTabToLocalStorage = (activeTab: string) =>
	localStorage.setItem(sessionKeys.dashboardActivetTab, activeTab);

export const getEventPageActiveTabFromLocalStorage = (): string => localStorage.getItem(sessionKeys.eventPageActiveTab);

export const setEventPageActiveTabToLocalStorage = (activeTab: string) =>
	localStorage.setItem(sessionKeys.eventPageActiveTab, activeTab);

export const getCommunityPageActiveTabFromLocalStorage = (): string =>
	localStorage.getItem(sessionKeys.communityPageActiveTab);

export const setCommunityPageActiveTabToLocalStorage = (activeTab: string) =>
	localStorage.setItem(sessionKeys.communityPageActiveTab, activeTab);

export const getLibraryActiveTabFromLocalStorage = (): string => localStorage.getItem(sessionKeys.libraryActiveTab);

export const setLibraryActiveTabToLocalStorage = (activeTab: string) =>
	localStorage.setItem(sessionKeys.libraryActiveTab, activeTab);

export const getPodcastActiveTabFromLocalStorage = (): string => localStorage.getItem(sessionKeys.podcastActiveTab);

export const setBookDetailScrollPositionToLocalStorage = (bookId: string, position: number): void => {
	const existingPositions = localStorage.getItem(sessionKeys.bookDetailScrollPosition);
	const scrollPositions: ScrollPositions = existingPositions ? JSON.parse(existingPositions) : {};
	scrollPositions[bookId] = position;
	localStorage.setItem(sessionKeys.bookDetailScrollPosition, JSON.stringify(scrollPositions)); // convert to JSON string
};

export const getBookDetailScrollPosition = (bookId: string): number => {
	const existingPositions = localStorage.getItem(sessionKeys.bookDetailScrollPosition);
	if (existingPositions) {
		const scrollPositions: ScrollPositions = JSON.parse(existingPositions); // convert to JavaScript object to find bookId
		return scrollPositions[bookId] || 0;
	}
	return 0;
};

export const getDocumentVaultPinVerifiedFromLocalStorage = (): boolean =>
	localStorage.getItem(sessionKeys.documentVaultPinVerified) === 'true';

export const setDocumentVaultPinVerifiedToLocalStorage = () =>
	localStorage.setItem(sessionKeys.documentVaultPinVerified, JSON.stringify(true));

export const getWrongDocumentVaultPinAttemptsFromLocalStorage = (): number =>
	safeParseInt(localStorage.getItem(sessionKeys.wrongDocumentVaultPinAttempts));

export const setWrongDocumentVaultPinAttemptsToLocalStorage = (count: number) =>
	localStorage.setItem(sessionKeys.wrongDocumentVaultPinAttempts, count.toString());

export const getDocumentVaultIdFromQuery = (location: { search: string }) => {
	return getUniqueQueryString(location.search, sessionKeys.documentVaultId);
};

export const getDocumentVaultShouldRefreshFromQuery = (location: { search: string }) => {
	return getUniqueQueryString(location.search, sessionKeys.documentVaultShouldRefresh);
};

export const getLastProfileModalShownFromLocalStorage = (): number =>
	safeParseInt(localStorage.getItem(sessionKeys.lastEventProfileModalShown));

export const setLastProfileModalShownToLocalStorage = (time: string) =>
	localStorage.setItem(sessionKeys.lastEventProfileModalShown, time);

export const getEventTourSkipTimestamp = (): number | null => {
	const value = localStorage.getItem(sessionKeys.joyrideEventTourSkipTimestamp);
	return value ? safeParseInt(value) : null;
};

export const setEventTourSkipTimestamp = (): void => {
	localStorage.setItem(sessionKeys.joyrideEventTourSkipTimestamp, Date.now().toString());
};

export const isEventTourSkipExpired = (durationInMs = tenHoursInMilliseconds): boolean => {
	const timestamp = getEventTourSkipTimestamp();
	if (!timestamp) return true;
	return Date.now() - timestamp >= durationInMs;
};

export const hasShownIntroModalThisSession = (): boolean =>
	localStorage.getItem(sessionKeys.joyrideEventTourIntroModalShown) === 'true';

export const markIntroModalAsShown = (): void =>
	localStorage.setItem(sessionKeys.joyrideEventTourIntroModalShown, 'true');

export const removeIntroModalAsShown = (): void => localStorage.removeItem(sessionKeys.joyrideEventTourIntroModalShown);

export const getLastMonthStepsSyncedFromLocalStorage = (year: number, month: number): boolean => {
	const syncKey = `${sessionKeys.lastMonthStepsSynced}_${year}_${month}`;
	return localStorage.getItem(syncKey) === 'true';
};

export const setLastMonthStepsSyncedToLocalStorage = (year: number, month: number): void => {
	const syncKey = `${sessionKeys.lastMonthStepsSynced}_${year}_${month}`;
	localStorage.setItem(syncKey, 'true');
};

export const clearApplicationStorage = () => {
	localStorage.removeItem(sessionKeys.refreshToken);
	localStorage.removeItem(sessionKeys.accessToken);
	localStorage.removeItem(sessionKeys.clientId);
	localStorage.removeItem(sessionKeys.dashboardActivetTab);
	localStorage.removeItem(sessionKeys.documentVaultPinVerified);
	localStorage.removeItem(sessionKeys.eventPageActiveTab);
	localStorage.removeItem(sessionKeys.joyrideEventTourSkipTimestamp);
	localStorage.removeItem('loginStatus'); // Clear Zustand persisted login status
	removeIntroModalAsShown();
};

export const clearRedirectUrlLocalStorage = () => {
	localStorage.removeItem(sessionKeys.redirectUrl);
};

export const clearPublicClientLocalStorage = () => {
	localStorage.removeItem(sessionKeys.publicClientProgress);
	localStorage.removeItem(sessionKeys.publicClientUniqueId);
};

export const clearRegistrationDataLocalStorage = () => {
	localStorage.removeItem(sessionKeys.registrationData);
};

export const clearAppGuidedTourLocalStorage = () => {
	localStorage.removeItem(sessionKeys.eventPageActiveTab);
	localStorage.removeItem(sessionKeys.joyrideEventTourSkipTimestamp);
	removeIntroModalAsShown();
};

interface QuestionAnswer {
	questionUniqueName: string;
	answer: string;
}

interface ScrollPositions {
	[key: string]: number;
}
